import type { ReactNode } from "react";
import { num, pct } from "../lib/format";
import type { Kpi, PageMeta } from "../lib/types";

// ═══════════════════════════════════════════════════════════════════════════
//  PRIMITIVES
// ═══════════════════════════════════════════════════════════════════════════

export function Card({
  title,
  note,
  aside,
  className = "",
  children,
}: {
  title?: string;
  note?: string;
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || aside) && (
        <header className="card-head">
          <div style={{ minWidth: 0 }}>
            {title && <h2 className="card-title">{title}</h2>}
            {note && <p className="card-note">{note}</p>}
          </div>
          {aside && <div className="card-aside">{aside}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * A headline number with its previous-period delta.
 *
 * The delta wording is the fiddly part and it is deliberate: an absent
 * comparison prints "no comparison" rather than "+100%", because the previous
 * window held zero and a percentage against nothing is not a fact. See the
 * note on `delta()` in the API.
 */
export function StatTile({
  label,
  value,
  kpi,
  hint,
  /** Set for metrics where DOWN is the good direction (failures, drop-off). */
  invert = false,
  /**
   * Set where neither direction is good or bad on its own. AI spend is the
   * case: it rises because more people are rendering, and painting that red
   * would read as a problem on the best week the product has had.
   */
  neutral = false,
}: {
  label: string;
  value: string | number;
  kpi?: Kpi;
  hint?: ReactNode;
  invert?: boolean;
  neutral?: boolean;
}) {
  const change = kpi?.change ?? null;
  const rising = !neutral && change !== null && change > 0.05;
  const falling = !neutral && change !== null && change < -0.05;
  const good = invert ? falling : rising;
  const bad = invert ? rising : falling;

  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <p className="stat-value">
        {typeof value === "number" ? num(value) : value}
      </p>
      <div className="stat-foot">
        {change === null ? (
          <span className="delta delta-flat">
            {kpi ? "no comparison" : ""}
          </span>
        ) : (
          <>
            <span
              className={`delta ${good ? "delta-up" : bad ? "delta-down" : "delta-flat"}`}
            >
              {change > 0 ? "▲" : change < 0 ? "▼" : "■"} {pct(Math.abs(change), 0)}
            </span>
            <span>vs previous period</span>
          </>
        )}
        {hint && <span>{hint}</span>}
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warning" | "critical";
  children: ReactNode;
}) {
  const cls =
    tone === "good"
      ? "badge badge-good"
      : tone === "critical"
        ? "badge badge-critical"
        : tone === "warning"
          ? "badge badge-warning"
          : "badge";
  return <span className={cls}>{children}</span>;
}

export function Banner({
  tone = "neutral",
  title,
  children,
}: {
  tone?: "neutral" | "warning" | "critical";
  title?: string;
  children: ReactNode;
}) {
  const cls =
    tone === "warning"
      ? "banner banner-warning"
      : tone === "critical"
        ? "banner banner-critical"
        : "banner";
  return (
    <div className={cls}>
      <div>
        {title && <strong>{title}</strong>}
        {children}
      </div>
    </div>
  );
}

/**
 * The one filter row, above everything it scopes.
 *
 * Deliberately not per-card: every panel on a page re-renders against the same
 * slice, so there is one control and no chance of two charts on one screen
 * quietly showing different windows.
 */
export const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
  { days: 365, label: "1y" },
] as const;

export function RangeFilter({
  days,
  onChange,
}: {
  days: number;
  onChange: (days: number) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label="Date range">
      {RANGES.map((r) => (
        <button
          key={r.days}
          type="button"
          aria-pressed={days === r.days}
          onClick={() => onChange(r.days)}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

export function Swatch({
  hex,
  small = false,
  title,
}: {
  hex: string;
  small?: boolean;
  title?: string;
}) {
  return (
    <span
      className={small ? "swatch swatch-sm" : "swatch"}
      style={{ background: hex }}
      title={title ?? hex}
    />
  );
}

/** Loading / error / empty, in the one order every panel needs them. */
export function PanelState({
  loading,
  error,
  empty,
  emptyText = "Nothing in this period.",
  children,
}: {
  loading: boolean;
  error: string | null;
  empty?: boolean;
  emptyText?: string;
  children: ReactNode;
}) {
  if (loading) return <Empty>Loading…</Empty>;
  if (error) return <Empty>{error}</Empty>;
  if (empty) return <Empty>{emptyText}</Empty>;
  return <>{children}</>;
}

/**
 * ── One pager for every list in the console ───────────────────────────────
 * Renders nothing at all when there is only one page, so a table of six rows
 * does not carry a disabled Previous/Next pair explaining that six rows is all
 * there is.
 *
 * The count is spelled out ("41–60 of 412") rather than shown as "page 3 of
 * 21". Page numbers answer a question nobody asked; what an admin actually
 * wants to know from a pager is how much is left.
 */
export function Pager({
  meta,
  onChange,
  busy = false,
  label = "rows",
}: {
  meta: PageMeta | undefined;
  onChange: (page: number) => void;
  busy?: boolean;
  label?: string;
}) {
  if (!meta || meta.total === 0) return null;

  const first = (meta.page - 1) * meta.pageSize + 1;
  const last = Math.min(meta.total, meta.page * meta.pageSize);

  // Nothing to page through — but the total is still worth saying.
  if (meta.pageCount <= 1) {
    return (
      <div className="pager">
        <span>
          {num(meta.total)} {label}
        </span>
      </div>
    );
  }

  return (
    <div className="pager">
      <span>
        {num(first)}–{num(last)} of {num(meta.total)} {label}
      </span>
      <button
        className="btn btn-sm"
        disabled={busy || meta.page <= 1}
        onClick={() => onChange(1)}
        aria-label="First page"
      >
        ‹‹
      </button>
      <button
        className="btn btn-sm"
        disabled={busy || meta.page <= 1}
        onClick={() => onChange(meta.page - 1)}
      >
        Previous
      </button>
      <button
        className="btn btn-sm"
        disabled={busy || !meta.hasMore}
        onClick={() => onChange(meta.page + 1)}
      >
        Next
      </button>
      <button
        className="btn btn-sm"
        disabled={busy || !meta.hasMore}
        onClick={() => onChange(meta.pageCount)}
        aria-label="Last page"
      >
        ››
      </button>
    </div>
  );
}
